import { BOT_GET_ORDERS_QUERY_KEY } from "@/api/bot/bot-get-orders";
import { useNotification } from "@/providers/notifications-bot-actions/notifications-bot-actions";
import {
  BotWalletSupportedChain,
  CodexChainId,
  useWallet,
} from "@/providers/wallet";
import { formatTokenAmountForDisplay } from "@/utils/currency";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";

type TradeEvent = {
  chainId?: number;
  currentTokenBalance?: number;
  nativeAmount?: string;
  nativePrice?: number;
  token?: string;
  tokenAmount?: string;
  tokenDecimals?: number;
  tokenName?: string;
  tokenSymbol?: string;
  txHash?: string;
  type?: string;
  duration?: string;
  pnlPercent?: number;
  pnlUsd?: number;
};

const mapToCodexChainId: Record<BotWalletSupportedChain, CodexChainId> = {
  [BotWalletSupportedChain.ETH]: CodexChainId.ETH,
  [BotWalletSupportedChain.SOL]: CodexChainId.SOL,
};

export const useTradeSocket = () => {
  const { currentWallet } = useWallet();

  const { setNotifyProps } = useNotification();

  const queryClient = useQueryClient();

  const socketRef = useRef<Socket | null>(null);
  const prevAddressRef = useRef<string | null>(null);

  const updateOrders = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [BOT_GET_ORDERS_QUERY_KEY],
      exact: false,
    });
  }, [queryClient]);

  const notify = useCallback(
    (trade: TradeEvent) => {
      const chainSymbol =
        trade.chainId === BotWalletSupportedChain.ETH ? "ETH" : "SOL";
      const codexChainId =
        mapToCodexChainId[trade.chainId as BotWalletSupportedChain];
      const link = `/screener/${trade.token}:${codexChainId}`;
      const tokenDecimals = trade.tokenDecimals ?? 6;
      const tokenAmount = trade.tokenAmount
        ? formatTokenAmountForDisplay(trade.tokenAmount, tokenDecimals)
        : "0";
      const nativeTokenAmount = trade.nativeAmount
        ? formatTokenAmountForDisplay(trade.nativeAmount, tokenDecimals)
        : "0";

      switch (trade.type) {
        case "limit-buy":
          updateOrders();
          setNotifyProps({
            type: "success",
            action: "limitBuy",
            params: {
              youReceive: tokenAmount,
              tokenSymbol: trade.tokenSymbol ?? "",
              youPayNative: nativeTokenAmount,
              chainSymbol,
              link,
              limitPrice: String(trade.nativePrice ?? "0"),
            },
          });
          break;
        case "limit-sell":
          updateOrders();
          setNotifyProps({
            type: "success",
            action: "limitSell",
            params: {
              youSell: tokenAmount,
              tokenSymbol: trade.tokenSymbol ?? "",
              youReceive: nativeTokenAmount,
              chainSymbol,
              pnl: trade.pnlUsd,
              roi: trade.pnlPercent,
              duration: trade.duration,
              currentBalance: trade.currentTokenBalance,
              link,
              limitPrice: String(trade.nativePrice ?? "0"),
            },
          });
          break;
        case "take-profit":
          updateOrders();
          setNotifyProps({
            type: "success",
            action: "takeProfit",
            params: {
              youSell: tokenAmount,
              tokenSymbol: trade.tokenSymbol ?? "",
              youReceive: nativeTokenAmount,
              chainSymbol,
              pnl: trade.pnlUsd,
              roi: trade.pnlPercent,
              duration: trade.duration,
              currentBalance: trade.currentTokenBalance,
              link,
              limitPrice: String(trade.nativePrice ?? "0"),
            },
          });
          break;
        case "stop-loss":
          updateOrders();
          setNotifyProps({
            type: "success",
            action: "stopLoss",
            params: {
              youSell: tokenAmount,
              tokenSymbol: trade.tokenSymbol ?? "",
              youReceive: nativeTokenAmount,
              chainSymbol,
              pnl: trade.pnlUsd,
              roi: trade.pnlPercent,
              duration: trade.duration,
              currentBalance: trade.currentTokenBalance,
              link,
              limitPrice: String(trade.nativePrice ?? "0"),
            },
          });
          break;
        case "copy-buy":
          updateOrders();
          setNotifyProps({
            type: "success",
            action: "copyBuy",
            params: {
              youReceive: tokenAmount,
              tokenSymbol: trade.tokenSymbol ?? "",
              youPayNative: nativeTokenAmount,
              chainSymbol,
              link,
              limitPrice: String(trade.nativePrice ?? "0"),
            },
          });
          break;
        case "copy-sell":
          setNotifyProps({
            type: "success",
            action: "copySell",
            params: {
              youSell: tokenAmount,
              tokenSymbol: trade.tokenSymbol ?? "",
              youReceive: nativeTokenAmount,
              chainSymbol,
              pnl: trade.pnlUsd,
              roi: trade.pnlPercent,
              duration: trade.duration,
              currentBalance: trade.currentTokenBalance,
              link,
              limitPrice: String(trade.nativePrice ?? "0"),
            },
          });
          break;
      }
    },
    [setNotifyProps, updateOrders]
  );

  const disconnect = () => {
    if (socketRef.current) {
      socketRef.current.off();
      socketRef.current.disconnect();
      socketRef.current = null;
      console.log("Socket disconnected");
    }
  };

  useEffect(() => {
    const address = currentWallet?.address;

    if (!address) {
      disconnect();
      prevAddressRef.current = null;
      return;
    }

    if (prevAddressRef.current === address && socketRef.current?.connected)
      return;

    disconnect();

    const socket = io(import.meta.env.VITE_TRADE_SOCKET_URL, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socketRef.current = socket;
    prevAddressRef.current = address;

    socket.on("connect", () => {
      console.log("Connected with id:", socket.id);

      socket.emit(
        "subscribe",
        JSON.stringify({
          walletAddresses: [address],
        })
      );

      console.log("Subscribed to wallets:", address);
    });

    socket.on("trade", ({ trade, walletAddress }) => {
      notify(trade);
      console.log(`Received trade for wallet ${walletAddress}:`, trade);
    });

    socket.on("disconnect", (reason) => {
      console.warn("Socket disconnected:", reason);
      if (reason === "io server disconnect") {
        socket.connect();
      }
    });

    socket.on("reconnect", () => {
      socket.emit("subscribe", JSON.stringify({ walletAddresses: [address] }));
    });

    const pingInterval = setInterval(() => {
      if (socket.connected) {
        socket.emit("ping");
      }
    }, 25000);

    return () => {
      clearInterval(pingInterval);
      disconnect();
    };
  }, [currentWallet, notify]);
};
